import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  type Company,
  type CostCategory,
  type CostDocument,
  type CostVatRow,
  type CorrectionCause,
  type Counterparty,
  type NewCost,
  type NewCostCorrection,
  type VatDeduction,
  type VatRateCode,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, currencyLabel, date, money, periodShort } from '../format';
import * as Icon from '../components/Icons';
import { CounterpartyDialog } from '../components/CounterpartyDialog';
import { ErrorAlert, failureOf, type Failure } from '../components/Alerts';
import { CostAccrualsPanel } from '../components/CostAccruals';
import { ProvisionsPanel } from '../components/Provisions';
import type { Panel } from '../components/Shell';

const CATEGORIES: CostCategory[] = ['GOODS', 'PURCHASE_COSTS', 'SALARIES', 'OTHER'];
const DEDUCTIONS: VatDeduction[] = ['FULL', 'HALF', 'NONE'];
const VAT_RATES: VatRateCode[] = ['23', '8', '5', '0', 'zw', 'np', 'oo'];

/** Kolumna KPiR wynika z rodzaju wydatku — tak samo jak na serwerze. */
const KPIR_COLUMN: Record<CostCategory, number> = {
  GOODS: 10,
  PURCHASE_COSTS: 11,
  SALARIES: 12,
  OTHER: 13,
};

const today = () => new Date().toISOString().slice(0, 10);

function emptyCost(): NewCost {
  return {
    documentNumber: '',
    counterpartyId: null,
    issueDate: today(),
    costDate: today(),
    category: 'OTHER',
    vatDeduction: 'FULL',
    fixedAsset: false,
    intraCommunity: false,
    description: '',
    vatRows: [{ vatRate: '23', net: '0.00', vat: '0.00' }],
  };
}

/**
 * Ile z dokumentu wejdzie w koszty i ile VAT-u da się odliczyć.
 *
 * Liczymy to po stronie przeglądarki wyłącznie po to, żeby pokazać rachunek **zanim**
 * dokument zostanie zapisany — wiążące kwoty przelicza serwer przy zapisie. W koszty wchodzi
 * netto powiększone o VAT, którego nie da się odliczyć: przy odliczeniu połowicznym
 * 398,37 zł netto daje 444,18 zł kosztu.
 */
function summarise(rows: CostVatRow[], deduction: VatDeduction) {
  const net = rows.reduce((sum, row) => sum + Number(row.net || 0), 0);
  const vat = rows.reduce((sum, row) => sum + Number(row.vat || 0), 0);
  const deductible = deduction === 'FULL' ? vat : deduction === 'HALF' ? vat / 2 : 0;
  return {
    net,
    vat,
    deductible,
    gross: net + vat,
    costAmount: net + (vat - deductible),
  };
}

export function CostsPanel({
  company,
  t,
  locale,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  onGo: (panel: Panel) => void;
}) {
  const [costs, setCosts] = useState<CostDocument[] | null>(null);
  const [suppliers, setSuppliers] = useState<Counterparty[]>([]);
  const [form, setForm] = useState<NewCost | null>(null);
  // Korekta kosztu podaje różnicę wprost — tak, jak stoi na dokumencie od dostawcy
  const [correction, setCorrection] = useState<{
    costId: string;
    number: string;
    form: NewCostCorrection;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);
  const [month, setMonth] = useState('');

  const load = useCallback(async () => {
    try {
      const [documents, contacts] = await Promise.all([
        api.costs(company.id),
        api.counterparties(company.id, '', false),
      ]);
      setCosts(documents);
      setSuppliers(contacts);
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const editable = !company.roles.every((role) => role === 'VIEWER' || role === 'BOARD_MEMBER');
  const supplierName = (id: string | null) => suppliers.find((item) => item.id === id)?.name ?? null;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  const months = useMemo(
    () => [...new Set((costs ?? []).map((cost) => cost.costDate.slice(0, 7)))].sort().reverse(),
    [costs],
  );
  const shown = (costs ?? []).filter((cost) => !month || cost.costDate.startsWith(month));
  const drafts = (costs ?? []).filter((cost) => cost.status === 'DRAFT');
  const draftsAmount = drafts.reduce((sum, cost) => sum + Number(cost.costAmount), 0);

  const totals = shown.reduce(
    (sum, cost) => ({
      net: sum.net + Number(cost.totalNet),
      vat: sum.vat + Number(cost.totalVat),
      deductible: sum.deductible + Number(cost.deductibleVat),
      costAmount: sum.costAmount + Number(cost.costAmount),
    }),
    { net: 0, vat: 0, deductible: 0, costAmount: 0 },
  );
  const unit = currencyLabel(shown[0]?.currency ?? 'PLN', locale);

  if (form) {
    return (
      <CostForm
        form={form}
        companyId={company.id}
        suppliers={suppliers}
        onSupplierAdded={(created) => setSuppliers((prev) => [...prev, created])}
        busy={busy}
        error={error?.message ?? null}
        t={t}
        locale={locale}
        onChange={setForm}
        onCancel={() => setForm(null)}
        onSave={(again) =>
          void run(async () => {
            await api.addCost(company.id, form);
            setForm(again ? emptyCost() : null);
          })
        }
      />
    );
  }

  if (correction) {
    return (
      <CorrectionForm
        correction={correction}
        busy={busy}
        error={error?.message ?? null}
        t={t}
        onChange={setCorrection}
        onCancel={() => setCorrection(null)}
        onSave={() =>
          void run(async () => {
            await api.correctCost(company.id, correction.costId, correction.form);
            setCorrection(null);
          })
        }
      />
    );
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.costs.title}</h3>
          <p>
            {drafts.length > 0
              ? `${t.costs.statuses.DRAFT}: ${drafts.length} · ${money(draftsAmount, locale)} ${t.costs.costAmount.toLowerCase()}`
              : `${shown.length} · ${money(totals.costAmount, locale)} ${t.costs.costAmount.toLowerCase()}`}
          </p>
        </div>
        {editable && (
          <div className="sp">
            {drafts.length > 1 && (
              <button
                type="button"
                className="btn sec"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    for (const draft of drafts) await api.bookCost(company.id, draft.id);
                  })
                }
              >
                {t.costs.bookAll}
              </button>
            )}
            <button type="button" className="btn" onClick={() => setForm(emptyCost())}>
              <Icon.Plus />
              {t.costs.new}
            </button>
          </div>
        )}
      </div>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      {/* Szkic nie wchodzi ani do KPiR, ani do VAT — to dlatego koszty na pulpicie bywają zerowe */}
      {drafts.length > 0 && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>
              {t.costs.draftsWaiting}: {drafts.length}
            </b>
            <p>{t.costs.draftsWaitingHint}</p>
          </div>
        </div>
      )}

      <div className="panel">
        <header>
          <h4>{t.costs.list}</h4>
          <div className="sp">
            {months.length > 1 && (
              <select
                className="inp w sm"
                aria-label={t.dashboard.month}
                value={month}
                onChange={(event) => setMonth(event.target.value)}
              >
                <option value="">{t.costs.allMonths}</option>
                {months.map((value) => (
                  <option key={value} value={value}>
                    {periodShort(value)}
                  </option>
                ))}
              </select>
            )}
          </div>
        </header>

        {shown.length === 0 ? (
          <div className="empty">
            <b>{t.costs.none}</b>
          </div>
        ) : (
          <>
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.costs.number}</th>
                    <th>{t.costs.supplier}</th>
                    <th>{t.costs.issueDate}</th>
                    <th>{t.costs.costDate}</th>
                    <th>{t.costs.category}</th>
                    <th className="n">
                      {t.costs.net} ({unit})
                    </th>
                    <th className="n">
                      {t.costs.vat} ({unit})
                    </th>
                    <th className="n">
                      {t.costs.deductible} ({unit})
                    </th>
                    <th className="n">
                      {t.costs.costAmount} ({unit})
                    </th>
                    <th>{t.costs.status}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((cost) => (
                    <tr key={cost.id}>
                      <td className="doc">
                        {cost.documentNumber}
                        {cost.documentKind === 'CORRECTION' && (
                          <span className="muted sm"> · {t.invoices.correction}</span>
                        )}
                      </td>
                      <td>{supplierName(cost.counterpartyId) ?? '—'}</td>
                      <td className="doc">{date(cost.issueDate, locale)}</td>
                      <td className="doc">{date(cost.costDate, locale)}</td>
                      <td className="muted sm">
                        {t.costs.column} {cost.kpirColumn}
                      </td>
                      <td className="n">{amount(cost.totalNet, locale)}</td>
                      <td className="n">{amount(cost.totalVat, locale)}</td>
                      <td className="n">{amount(cost.deductibleVat, locale)}</td>
                      <td className="n">{amount(cost.costAmount, locale)}</td>
                      <td>
                        <span className={cost.status === 'BOOKED' ? 'chip st-ok' : 'chip st-draft'}>
                          <i />
                          {t.costs.statuses[cost.status]}
                        </span>
                      </td>
                      <td className="act">
                        <div className="row">
                          {editable && cost.status === 'BOOKED' && cost.documentKind === 'COST' && (
                            <button
                              type="button"
                              className="btn ghost sm"
                              disabled={busy}
                              onClick={() =>
                                setCorrection({
                                  costId: cost.id,
                                  number: cost.documentNumber,
                                  form: {
                                    documentNumber: '',
                                    issueDate: today(),
                                    costDate: today(),
                                    cause: 'MISTAKE',
                                    reason: '',
                                    vatRows: cost.vatRows.map((row) => ({ ...row, net: '0.00', vat: '0.00' })),
                                  },
                                })
                              }
                            >
                              {t.invoices.correct}
                            </button>
                          )}
                          {editable && cost.status === 'DRAFT' && (
                            <>
                              <button
                                type="button"
                                className="btn ghost sm"
                                disabled={busy}
                                onClick={() => void run(() => api.bookCost(company.id, cost.id))}
                              >
                                {t.costs.book}
                              </button>
                              {/* Kasowanie szkicu ikoną: przy dziesięciu kolumnach z kwotami
                                  przyklejona kolumna czynności zasłaniała ostatnią z nich */}
                              <button
                                type="button"
                                className="btn danger sm ico"
                                disabled={busy}
                                aria-label={t.costs.delete}
                                title={t.costs.delete}
                                onClick={() => void run(() => api.deleteCost(company.id, cost.id))}
                              >
                                <Icon.Trash />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5}>
                      {t.invoices.totals} · {shown.length}
                    </td>
                    <td className="n">{amount(totals.net, locale)}</td>
                    <td className="n">{amount(totals.vat, locale)}</td>
                    <td className="n">{amount(totals.deductible, locale)}</td>
                    <td className="n">{amount(totals.costAmount, locale)}</td>
                    <td />
                    <td className="act" />
                  </tr>
                </tfoot>
              </table>
            </div>
            {/* Kolumny „VAT" i „VAT do odliczenia" stoją obok siebie, żeby różnica była widoczna
                bez otwierania dokumentu */}
            <p className="note">{t.costs.hint}</p>
          </>
        )}
      </div>

      {/*
        Rozliczenia międzyokresowe stoją przy kosztach, bo harmonogram zakłada się na dokumencie
        kosztowym. Tylko przy pełnych księgach: przy KPiR i ryczałcie nie ma konta 640 ani dziennika,
        więc panel kończyłby się komunikatem „ta firma ma inny rodzaj ksiąg".
      */}
      {company.booksType === 'FULL' && (
        <>
          <CostAccrualsPanel company={company} t={t} locale={locale} editable={editable} onGo={onGo} />
          {/*
            Rozliczenia bierne stoją obok czynnych, bo to ta sama sprawa z dwóch stron: tu koszt
            czeka na miesiące, tam miesiąc czeka na koszt. Rozdzielenie ich po dwóch ekranach
            znaczyłoby, że księgowa szuka dwa razy tego samego.
          */}
          <ProvisionsPanel company={company} t={t} locale={locale} editable={editable} onGo={onGo} />
        </>
      )}
    </>
  );
}

/** Ekran 15: dokument, kwalifikacja podatkowa i rozbicie na stawki — trzy grupy, nie jedna lista pól. */
function CostForm({
  form,
  companyId,
  suppliers,
  onSupplierAdded,
  busy,
  error,
  t,
  locale,
  onChange,
  onCancel,
  onSave,
}: {
  form: NewCost;
  companyId: string;
  suppliers: Counterparty[];
  onSupplierAdded: (created: Counterparty) => void;
  busy: boolean;
  error: string | null;
  t: Dictionary;
  locale: Locale;
  onChange: (form: NewCost) => void;
  onCancel: () => void;
  onSave: (again: boolean) => void;
}) {
  const [addingSupplier, setAddingSupplier] = useState(false);
  const sum = summarise(form.vatRows, form.vatDeduction);

  const updateRow = (index: number, change: Partial<CostVatRow>) =>
    onChange({
      ...form,
      vatRows: form.vatRows.map((row, position) => (position === index ? { ...row, ...change } : row)),
    });

  return (
    <>
      {addingSupplier && (
        <CounterpartyDialog
          companyId={companyId}
          t={t}
          onCreated={(created) => {
            onSupplierAdded(created);
            onChange({ ...form, counterpartyId: created.id });
            setAddingSupplier(false);
          }}
          onClose={() => setAddingSupplier(false)}
        />
      )}
      <div className="hd">
        <div>
          <h3>{t.costs.new}</h3>
          <p>{t.costs.draftAfterSave}</p>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      <div className="grid aside">
        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            onSave(false);
          }}
        >
          <header>
            <h4>{t.costs.document}</h4>
          </header>
          <div className="fields">
            <div className="field">
              <label htmlFor="kNumber">{t.costs.number}</label>
              <input
                id="kNumber"
                className="inp"
                value={form.documentNumber}
                onChange={(event) => onChange({ ...form, documentNumber: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="kSupplier">{t.costs.supplier}</label>
              <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                <select
                  id="kSupplier"
                  className="inp"
                  value={form.counterpartyId ?? ''}
                  onChange={(event) => onChange({ ...form, counterpartyId: event.target.value || null })}
                >
                  <option value="">{t.costs.chooseSupplier}</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
                {/* Faktura od nowego dostawcy to normalny przypadek, a nie wyjątek */}
                <button
                  type="button"
                  className="btn sec"
                  title={t.counterparties.add}
                  aria-label={t.counterparties.add}
                  onClick={() => setAddingSupplier(true)}
                >
                  <Icon.Plus />
                </button>
              </div>
              <span className="hint">{t.costs.supplierHint}</span>
            </div>
            <div className="field">
              <label htmlFor="kIssue">{t.costs.issueDate}</label>
              <input
                id="kIssue"
                className="inp"
                type="date"
                value={form.issueDate}
                onChange={(event) => onChange({ ...form, issueDate: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="kCost">{t.costs.costDate}</label>
              <input
                id="kCost"
                className="inp"
                type="date"
                value={form.costDate}
                onChange={(event) => onChange({ ...form, costDate: event.target.value })}
                required
              />
              <span className="hint">{t.costs.costDateHint}</span>
            </div>
          </div>

          <header style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: 'var(--gap)' }}>
            <h4>{t.costs.qualification}</h4>
          </header>
          <div className="fields">
            <div className="field">
              <label htmlFor="kCategory">{t.costs.category}</label>
              <select
                id="kCategory"
                className="inp"
                value={form.category}
                onChange={(event) => onChange({ ...form, category: event.target.value as CostCategory })}
              >
                {CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {t.costs.categories[category]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="kDeduction">{t.costs.deduction}</label>
              <select
                id="kDeduction"
                className="inp"
                value={form.vatDeduction}
                onChange={(event) => onChange({ ...form, vatDeduction: event.target.value as VatDeduction })}
              >
                {DEDUCTIONS.map((deduction) => (
                  <option key={deduction} value={deduction}>
                    {t.costs.deductions[deduction]}
                  </option>
                ))}
              </select>
            </div>
            {/* Deklaracja VAT wykazuje nabycia środków trwałych w osobnych polach */}
            <div className="field">
              <label htmlFor="kAsset">{t.costs.fixedAsset}</label>
              <label className="check" htmlFor="kAsset">
                <input
                  id="kAsset"
                  type="checkbox"
                  checked={form.fixedAsset}
                  onChange={(event) => onChange({ ...form, fixedAsset: event.target.checked })}
                />
                {t.costs.fixedAssetHint}
              </label>
            </div>
            {/*
              Do informacji VAT-UE wchodzą nabycia **towarów**, a nie import usług — z dokumentu
              tego nie widać, więc pytamy wprost, tak samo jak o środek trwały
            */}
            <div className="field">
              <label htmlFor="kWnt">{t.costs.intraCommunity}</label>
              <label className="check" htmlFor="kWnt">
                <input
                  id="kWnt"
                  type="checkbox"
                  checked={form.intraCommunity}
                  onChange={(event) => onChange({ ...form, intraCommunity: event.target.checked })}
                />
                {t.costs.intraCommunityHint}
              </label>
            </div>
          </div>

          {/* Kwoty wpisujemy w rozbiciu na stawki — tak wygląda faktura kosztowa */}
          <header style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: 'var(--gap)' }}>
            <h4>{t.costs.rates}</h4>
            <div className="sp">
              <button
                type="button"
                className="btn ghost sm"
                onClick={() =>
                  onChange({ ...form, vatRows: [...form.vatRows, { vatRate: '23', net: '0.00', vat: '0.00' }] })
                }
              >
                <Icon.Plus />
                {t.costs.addRate}
              </button>
            </div>
          </header>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 130 }}>{t.invoices.vatRate}</th>
                  <th className="n">{t.costs.net}</th>
                  <th className="n">{t.costs.vat}</th>
                  <th className="n">{t.invoices.grossAmount}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {form.vatRows.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <select
                        className="inp sm"
                        value={row.vatRate}
                        onChange={(event) => updateRow(index, { vatRate: event.target.value as VatRateCode })}
                        aria-label={t.invoices.vatRate}
                      >
                        {VAT_RATES.map((rate) => (
                          <option key={rate} value={rate}>
                            {t.products.rates[rate]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="n">
                      <input
                        className="inp num sm"
                        value={row.net}
                        onChange={(event) => updateRow(index, { net: event.target.value })}
                        inputMode="decimal"
                        aria-label={t.costs.net}
                        required
                      />
                    </td>
                    <td className="n">
                      <input
                        className="inp num sm"
                        value={row.vat}
                        onChange={(event) => updateRow(index, { vat: event.target.value })}
                        inputMode="decimal"
                        aria-label={t.costs.vat}
                        required
                      />
                    </td>
                    <td className="n">{amount(Number(row.net || 0) + Number(row.vat || 0), locale)}</td>
                    <td className="act">
                      {form.vatRows.length > 1 && (
                        <button
                          type="button"
                          className="btn danger sm"
                          onClick={() =>
                            onChange({ ...form, vatRows: form.vatRows.filter((_, i) => i !== index) })
                          }
                        >
                          {t.invoices.removeLine}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>{t.invoices.totals}</td>
                  <td className="n">{amount(sum.net, locale)}</td>
                  <td className="n">{amount(sum.vat, locale)}</td>
                  <td className="n">{amount(sum.gross, locale)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="bar2">
            <button type="submit" className="btn" disabled={busy}>
              {busy ? t.companies.saving : t.costs.save}
            </button>
            {/* Koszty wprowadza się seriami — po zapisaniu zwykle wpisuje się kolejny */}
            <button type="button" className="btn sec" disabled={busy} onClick={() => onSave(true)}>
              {t.costs.saveAndNext}
            </button>
            <div className="sp">
              <button type="button" className="btn ghost" disabled={busy} onClick={onCancel}>
                {t.companies.cancel}
              </button>
            </div>
          </div>
        </form>

        <div className="grid">
          <div className="panel">
            <header>
              <h4>{t.costs.summary}</h4>
            </header>
            <div className="sum">
              <div className="r">
                <span className="muted">{t.costs.net}</span>
                <b>{money(sum.net, locale)}</b>
              </div>
              <div className="r">
                <span className="muted">{t.costs.vatInput}</span>
                <b>{money(sum.vat, locale)}</b>
              </div>
              <div className="r">
                <span className="muted">{t.costs.deductible}</span>
                <b>{money(sum.deductible, locale)}</b>
              </div>
              <div className="r">
                <span className="muted">{t.invoices.grossAmount}</span>
                <b>{money(sum.gross, locale)}</b>
              </div>
              <div className="r tot">
                <span>{t.costs.costAmount}</span>
                <b>{money(sum.costAmount, locale)}</b>
              </div>
            </div>
            <p className="hint">{t.costs.hint}</p>
          </div>

          {/* Na co ten dokument wpłynie — dziś trzeba w to wierzyć, tu widać wprost */}
          <div className="panel">
            <header>
              <h4>{t.costs.goesTo}</h4>
            </header>
            <dl className="brk">
              <dt>{t.costs.kpir}</dt>
              <dd>
                {t.costs.column} {KPIR_COLUMN[form.category]} · {periodShort(form.costDate.slice(0, 7))}
              </dd>
              <dt>{t.costs.vatRegister}</dt>
              <dd>
                {t.costs.purchases} · {periodShort(form.issueDate.slice(0, 7))}
              </dd>
            </dl>
            <p className="hint">{t.costs.draftAfterSaveHint}</p>
          </div>

          <div className="panel future">
            <div className="hd2">
              <b>{t.costs.scanFutureTitle}</b>
              <span className="soon">{t.invoices.soon}</span>
            </div>
            <p className="sm muted">{t.costs.scanFutureText}</p>
          </div>
        </div>
      </div>
    </>
  );
}

/** Korekta kosztu: kwoty różnicy, tak jak stoją na dokumencie od dostawcy. */
function CorrectionForm({
  correction,
  busy,
  error,
  t,
  onChange,
  onCancel,
  onSave,
}: {
  correction: { costId: string; number: string; form: NewCostCorrection };
  busy: boolean;
  error: string | null;
  t: Dictionary;
  onChange: (next: { costId: string; number: string; form: NewCostCorrection }) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const setForm = (change: Partial<NewCostCorrection>) =>
    onChange({ ...correction, form: { ...correction.form, ...change } });

  const updateRow = (index: number, change: Partial<CostVatRow>) =>
    setForm({
      vatRows: correction.form.vatRows.map((row, position) =>
        position === index ? { ...row, ...change } : row,
      ),
    });

  return (
    <>
      <div className="hd">
        <div>
          <h3>
            {t.costs.correctionTo} {correction.number}
          </h3>
          <p>{t.costs.correctionHint}</p>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      <form
        className="panel"
        onSubmit={(event) => {
          event.preventDefault();
          onSave();
        }}
      >
        <div className="fields">
          <div className="field">
            <label htmlFor="krNumber">{t.costs.number}</label>
            <input
              id="krNumber"
              className="inp"
              value={correction.form.documentNumber}
              onChange={(event) => setForm({ documentNumber: event.target.value })}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="krIssue">{t.costs.issueDate}</label>
            <input
              id="krIssue"
              className="inp"
              type="date"
              value={correction.form.issueDate}
              onChange={(event) => setForm({ issueDate: event.target.value })}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="krCost">{t.costs.costDate}</label>
            <input
              id="krCost"
              className="inp"
              type="date"
              value={correction.form.costDate}
              onChange={(event) => setForm({ costDate: event.target.value })}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="krCause">{t.invoices.correctionCause}</label>
            <select
              id="krCause"
              className="inp"
              value={correction.form.cause}
              onChange={(event) => setForm({ cause: event.target.value as CorrectionCause })}
            >
              <option value="MISTAKE">{t.costs.causes.MISTAKE}</option>
              <option value="EVENT">{t.costs.causes.EVENT}</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="krReason">{t.invoices.correctionReason}</label>
            <input
              id="krReason"
              className="inp"
              value={correction.form.reason}
              onChange={(event) => setForm({ reason: event.target.value })}
              required
            />
          </div>
        </div>

        <header style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: 'var(--gap)' }}>
          <h4>{t.costs.rates}</h4>
        </header>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th style={{ width: 130 }}>{t.invoices.vatRate}</th>
                <th className="n">{t.costs.net}</th>
                <th className="n">{t.costs.vat}</th>
              </tr>
            </thead>
            <tbody>
              {correction.form.vatRows.map((row, index) => (
                <tr key={index}>
                  <td>
                    <select
                      className="inp sm"
                      value={row.vatRate}
                      onChange={(event) => updateRow(index, { vatRate: event.target.value as VatRateCode })}
                      aria-label={t.invoices.vatRate}
                    >
                      {VAT_RATES.map((rate) => (
                        <option key={rate} value={rate}>
                          {t.products.rates[rate]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="n">
                    <input
                      className="inp num sm"
                      value={row.net}
                      onChange={(event) => updateRow(index, { net: event.target.value })}
                      inputMode="decimal"
                      aria-label={t.costs.net}
                      required
                    />
                  </td>
                  <td className="n">
                    <input
                      className="inp num sm"
                      value={row.vat}
                      onChange={(event) => updateRow(index, { vat: event.target.value })}
                      inputMode="decimal"
                      aria-label={t.costs.vat}
                      required
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bar2">
          <button type="submit" className="btn" disabled={busy}>
            {busy ? t.companies.saving : t.costs.save}
          </button>
          <div className="sp">
            <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
              {t.companies.cancel}
            </button>
          </div>
        </div>
      </form>
    </>
  );
}
